import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import SubHeader from "components/common/SubHeader";
import { getTravelList } from "api/travel";
import useShare from "hooks/useShare";
import homeIcon from "assets/images/icons/ic_home_line.svg";
// import shareIcon from "assets/images/icons/ic_share.svg";
import {getBarType, getTourColor, fmtDateRangeKo} from './util'
import "assets/scss/pages/TourCalendar.scss";
import Seo from "components/common/Seo";

dayjs.extend(isBetween);

const MIN_TOURS_PER_MONTH = 3; // 이 개수 미만이면 다음달 자동 체인 로드
const MAX_CHAIN = 3; // 한 번의 트리거당 체인 최대 3회 (안전장치)
const MAX_LOADED_MONTHS = 11; // 베이스 1 + 추가 11 = 총 12개월까지만 로드


function TourCalendarPage() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const { share } = useShare();

    // URL ?month=YYYY-MM 과 동기화되는 월 — 외부 공유/복귀 시 초기값 기준
    const [selectedMonth, setSelectedMonth] = useState(() => {
        const fromUrl = searchParams.get('month');
        if (fromUrl && /^\d{4}-\d{2}$/.test(fromUrl) && dayjs(fromUrl).isValid()) {
            return fromUrl;
        }
        return dayjs().format('YYYY-MM');
    });

    const [tourList, setTourList] = useState([]);
    const [activeStartDate, setActiveStartDate] = useState(() => dayjs(selectedMonth).toDate());
    const [view, setView] = useState('month'); // 'month' | 'year' | 'decade'
    const [activeTab, setActiveTab] = useState('RECRUITING'); // 'RECRUITING' | 'ALWAYS'
    const [autoLoadEnabled, setAutoLoadEnabled] = useState(false); // 더보기 버튼
    
    const isLoadingRef = useRef(false);
    const loadedMonthsRef = useRef(0); // 베이스 월 이후 추가로 로드된 개월 수 (체인/스크롤에서 동기 추적)

 
    const navLabel = useMemo(() => {
        const d = dayjs(activeStartDate);
        if (view === 'month') return d.format('YYYY년 M월');
        if (view === 'year') return d.format('YYYY년');
        const startYear = Math.floor(d.year() / 10) * 10;
        return `${startYear} - ${startYear + 9}`;
    }, [activeStartDate, view]);

    // month view 에서만 selectedMonth(=URL 쿼리)를 함께 갱신,
    // year/decade view 이동은 activeStartDate 만 변경
    const shiftDate = (direction) => {
        const amount = view === 'decade' ? 10 : 1;
        const unit = view === 'month' ? 'month' : 'year';
        const next = dayjs(activeStartDate)[direction](amount, unit);

        setActiveStartDate(next.toDate());
        if (view === 'month') {
            setSelectedMonth(next.format('YYYY-MM'));
        }
    };

    const handlePrev = () => shiftDate('subtract');
    const handleNext = () => shiftDate('add');

    const handleLabelClick = () => {
        if (view === 'month') setView('year');
        else if (view === 'year') setView('decade');
    };

    // selectedMonth → URL 쿼리스트링 동기화 (replace 로 히스토리 오염 방지)
    useEffect(() => {
        setSearchParams((prev) => {
            prev.set('month', selectedMonth);
            return prev;
        }, { replace: true });
    }, [selectedMonth, setSearchParams]);

    // selectedMonth 변경 시: 리스트 초기화 + 해당 월만 로드 + 자동 로드 비활성화
    useEffect(() => {
        isLoadingRef.current = true;
        loadedMonthsRef.current = 0;
        setAutoLoadEnabled(false);
        getTravelList(selectedMonth).then((res) => {
            if (res && res.results) setTourList(res.results);
            else setTourList([]);
            isLoadingRef.current = false;
        }).catch(() => {
            isLoadingRef.current = false;
        });
    }, [selectedMonth]);

    // 다음 달 추가 로드 (append + 중복 제거)
    // 결과가 MIN_TOURS_PER_MONTH 미만이면 다음달 자동 체인 로드 (chainDepth로 안전장치)
    const loadMoreMonth = useCallback((chainDepth = 0) => {
        if (isLoadingRef.current) {
            return;
        }
        if (loadedMonthsRef.current >= MAX_LOADED_MONTHS) {
            return;
        }
        if (chainDepth >= MAX_CHAIN) {
            return;
        }
        isLoadingRef.current = true;
        const nextOffset = loadedMonthsRef.current + 1;
        const nextMonth = dayjs(selectedMonth).add(nextOffset, 'month').format('YYYY-MM');

        getTravelList(nextMonth).then((res) => {
            const results = (res && res.results.filter(tour => tour.tour_type !== "ALWAYS")) || [];
            if (results.length) {
                setTourList((prev) => {
                    const ids = new Set(prev.map((t) => t.id));
                    const additions = results.filter((t) => !ids.has(t.id));
                    return additions.length ? [...prev, ...additions] : prev;
                });
            }
            loadedMonthsRef.current = nextOffset;
            isLoadingRef.current = false;

            // 결과 0개 또는 3개 미만이면 다음달 자동 체인 로드
            if (results.length < MIN_TOURS_PER_MONTH) {
                loadMoreMonth(chainDepth + 1);
            }
        }).catch((err) => {
            isLoadingRef.current = false;
        });
    }, [selectedMonth]);

    // 스크롤 자동 로드 (더보기 1회 클릭 후 활성화)
    // PullToRefresh 내부의 .ptr-scroll-container 가 실제 스크롤 컨테이너이므로 거기에 부착
    useEffect(() => {
        if (!autoLoadEnabled) {
            return;
        }
        const container = document.querySelector('.ptr-scroll-container');
        if (!container) {
            return;
        }

        const onScroll = () => {
            const scrollTop = container.scrollTop;
            const clientHeight = container.clientHeight;
            const scrollHeight = container.scrollHeight;
            const distanceToBottom = scrollHeight - (scrollTop + clientHeight);
            if (isLoadingRef.current) return;
            if (distanceToBottom <= 300) {
                loadMoreMonth();
            }
        };
        container.addEventListener('scroll', onScroll, { passive: true });
        return () => {
            container.removeEventListener('scroll', onScroll);
        };
    }, [autoLoadEnabled, loadMoreMonth]);

    // 탭 기준 필터링
    const filteredTours = useMemo(() => {
        if (activeTab === 'ALWAYS') {
            return tourList.filter((t) => t.tour_type === 'ALWAYS');
        }
        return tourList.filter((t) => t.tour_type !== 'ALWAYS');
    }, [tourList, activeTab]);

    // LIMITED 투어 + ALWAYS 투어의 각 회차를 캘린더 항목으로 통합
    const calendarItems = useMemo(() => {
        const items = [];
        filteredTours.forEach((tour) => {
            if (tour.start_date) {
                // LIMITED 투어 — 기존 동작
                items.push({ id: tour.id, start_date: tour.start_date, end_date: tour.end_date, name: tour.name, location: tour.location });
            } else if (Array.isArray(tour.departures_summary) && tour.departures_summary.length > 0) {
                // ALWAYS 투어 — 각 회차를 개별 항목으로 추가
                tour.departures_summary.forEach((dep) => {
                    if (dep.start_date) {
                        items.push({ id: tour.id, start_date: dep.start_date, end_date: dep.end_date, name: tour.name, location: tour.location });
                    }
                });
            }
        });
        return items;
    }, [filteredTours]);

    const handleBarClick = useCallback((e, tourId) => {
        e.stopPropagation();
        navigate(`/tour/detail/${tourId}`)
    }, []);


    const clickNextTour = () => {
        setAutoLoadEnabled(true);
        loadMoreMonth();
    };

    const tileContent = useCallback(
        ({ date, view }) => {
            if (view !== "month") return null;

            const bars = calendarItems
                .map((tour, idx) => {
                    const type = getBarType(date, tour);
                    if (!type) return null;
                    // 시작 segment(bs/bse)에만 라벨 표시 — 주 시작(일요일)에도 반복
                    const showLabel = type === 'bs' || type === 'bse';
                    return (
                        <div
                            key={`${tour.id}-${idx}`}
                            className={`tc-bar ${type}`}
                            style={{ backgroundColor: getTourColor(tour.id) }}
                            onClick={(e) => handleBarClick(e, tour.id)}
                        >
                            {showLabel && (
                                <span className="tc-bar-label">
                                    {tour.location ? `${tour.location} ` : ''}
                                </span>
                            )}
                        </div>
                    );
                })
                .filter(Boolean);

            if (bars.length === 0) return null;

            return <div className="tc-bars">{bars}</div>;
        },
        [calendarItems, handleBarClick]
    );

    const handleDetailClick = (e, id) => {
        e.stopPropagation();
        navigate(`/tour/detail/${id}`);
    };

    return (
        <div className="tour-calendar-page">
            <Seo
                title="투어 캘린더"
                description="국내외 프리다이빙·스쿠버다이빙 투어 일정을 캘린더로 한눈에 확인하고 예약하세요. 더딥스, 와이낫다이브의 투어 일정을 월별로 제공합니다."
            />
            <SubHeader
                title="투어 캘린더"
                menus={[{
                  name: "홈",
                  icon: homeIcon,
                  callback: () => {
                      if (window.location.pathname !== "/") navigate("/");
                  },
              },]}
            />

            {/* ── 월 네비게이션 (sticky) ── */}
            <div className="tc-calendar-nav">
                <button type="button" className="tc-nav-btn" onClick={handlePrev}>‹</button>
                <button type="button" className="tc-nav-label" onClick={handleLabelClick}>
                    {navLabel}
                </button>
                <button type="button" className="tc-nav-btn" onClick={handleNext}>›</button>
            </div>

            {/* ── 캘린더 섹션 ── */}
            <div className="tc-calendar-section">
                <div className="tc-calendar-wrap">
                    <Calendar
                        locale="ko-KR"
                        calendarType="gregory"
                        view={view}
                        onViewChange={({ view: v }) => setView(v)}
                        minDetail="decade"
                        maxDetail="month"
                        showNavigation={false}
                        activeStartDate={activeStartDate}
                        onActiveStartDateChange={({ activeStartDate: d, view: newView }) => {
                            setActiveStartDate(d);
                            if (newView === 'month') {
                                setSelectedMonth(dayjs(d).format('YYYY-MM'));
                            }
                        }}
                        tileContent={tileContent}
                        formatDay={(locale, date) => dayjs(date).format("D")}
                        showNeighboringMonth={true}
                    />
                </div>

            </div>

            {/* ── 탭 ── */}
            <div className="tc-tabs">
                <button
                    type="button"
                    className={`tc-tab ${activeTab === 'RECRUITING' ? 'active' : ''}`}
                    onClick={() => setActiveTab('RECRUITING')}
                >
                    모집중인 투어
                </button>
                <button
                    type="button"
                    className={`tc-tab ${activeTab === 'ALWAYS' ? 'active' : ''}`}
                    onClick={() => setActiveTab('ALWAYS')}
                >
                    상시투어
                </button>
            </div>

            {/* ── 투어 리스트 섹션 ── */}
            <div className="tc-list-section">
                <div className="tc-list-header">
                    <h3>{activeTab === 'ALWAYS' ? '상시 투어 ✈️' : '이번달 투어 ✈️'}</h3>
                    <span>총 {filteredTours.length}개</span>
                </div>

                {filteredTours.length === 0 ? (
                    <div className="tc-empty">
                        {activeTab === 'ALWAYS'
                            ? '현재 상시 투어가 없습니다.'
                            : '이번달 모집 중인 투어가 없습니다.'}
                    </div>
                ) : (
                    filteredTours.map((tour) => {
                        const color = getTourColor(tour.id);
                        const guide = tour.guide_names.join('&') ?? null;
                        const dateText = fmtDateRangeKo(tour.start_date, tour.end_date);
                        const countText = tour.tour_type === 'ALWAYS'
                            ? `회차당 ${tour.max_participants}명`
                            : `잔여 ${Math.max(0, (tour.max_participants ?? 0) - (tour.confirmed_count ?? 0))}자리`;
                        return (
                            <div
                                key={tour.id}
                                id={`tc-card-${tour.id}`}
                                className="tc-list-item"
                                onClick={(e) => handleDetailClick(e, tour.id)}
                            >
                                <span
                                    className="tc-item-bullet"
                                    aria-hidden="true"
                                    style={{ backgroundColor: color }}
                                />

                                <div className="tc-item-info">
                                    <div className="tc-item-date">{dateText ?? '상시모집'}</div>
                                    <div className="tc-item-title">{tour.name}</div>
                                  
                                    <div className="tc-item-subtitle">{tour.location}  {tour.price>0 &&` · 예약금 ${Number(tour.price).toLocaleString('ko-KR')}원`}</div>
                                    <div className="tc-item-meta">
                                        <span className="tc-meta-text">{guide ? guide : '미정'}</span>
                                        <span className="tc-meta-dot">·</span>
                                        <span className="tc-meta-text">
                                            {tour.tour_type !== 'ALWAYS' && `${tour.confirmed_count ?? 0}명 신청`}
                                        </span>
                                        <span className="tc-meta-text">
                                            <strong>{countText}</strong>
                                        </span>
                                    </div>
                                </div>

                                <div
                                    className="tc-item-img"
                                    style={
                                        !tour.thumbnail_image
                                            ? { background: `linear-gradient(135deg, ${color}, ${color}aa)` }
                                            : {}
                                    }
                                >
                                    {tour.thumbnail_image ? (
                                        <img
                                            src={tour.thumbnail_image}
                                            alt={tour.name}
                                        />
                                    ) : (
                                        <span>✈️</span>
                                    )}
                                </div>
                            </div>
                        );
                    })
                )}

                {activeTab === 'RECRUITING'  && !autoLoadEnabled && (
                    <button
                        type="button"
                        className="btn btn-small font-text3"
                        onClick={clickNextTour}
                    >
                        다음 투어 더보기 ▾
                    </button>
                )}
            </div>
        </div>
    );
}

export default TourCalendarPage;
